import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUpload, faFile, faRobot, faSpinner, faRotate } from '@fortawesome/free-solid-svg-icons';
import './UploadDocumentModal.css';
import { repositoryService } from '../../services/repositoryService';
import { toast } from 'react-toastify';
import apiHelper from '../../utils/apiHelper';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const UploadDocumentModal = ({ onClose, onUpload }) => {
    const [formData, setFormData] = useState({
        title: '',
        summary: '',
        type: '',
        workingGroup: '',
        file: null,
        fileName: '',
        month: '',
        year: new Date().getFullYear(),
        mpKey: '',
        qtr: '',
        status: 'Published',
        publish: true,
        publishDate: new Date().toISOString().split('T')[0],
        useAiSummary: false,
        doNotShowInSources: false
    });

    // Add all state declarations together at the top
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [mpKeyOptions, setMpKeyOptions] = useState([]);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [workingGroups, setWorkingGroups] = useState([]);
    const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
    const [currentFile, setCurrentFile] = useState(null);
    const [isAiSummaryAvailable, setIsAiSummaryAvailable] = useState(false);
    const [generateSummaryOnUpload, setGenerateSummaryOnUpload] = useState(true); // Checked by default

    useEffect(() => {
        const fetchChoices = async () => {

            
            try {
                const [mpKeys, docTypes, workGroups] = await Promise.all([
                    repositoryService.getMPKeyChoices(),
                    repositoryService.getDocumentTypeChoices(),
                    repositoryService.getWorkingGroupChoices()
                ]);

                setMpKeyOptions(mpKeys);
                setDocumentTypes(docTypes);
                setWorkingGroups(workGroups);
            } catch (error) {
                console.error('Error fetching choices:', error);
            }
        };
        
        fetchChoices();
    }, []);

    // Helper function to generate summary from PDF
    const generateSummaryFromPDF = async (file, title) => {
        if (!file) return null;
        
        setIsGeneratingSummary(true);
        try {
            // Read the PDF file
            const fileReader = new FileReader();
            
            const pdfText = await new Promise((resolve, reject) => {
                fileReader.onload = async (event) => {
                    try {
                        const typedarray = new Uint8Array(event.target.result);
                        const pdf = await pdfjsLib.getDocument({ data: typedarray }).promise;
                        
                        let fullText = '';
                        for (let i = 1; i <= pdf.numPages; i++) {
                            const page = await pdf.getPage(i);
                            const textContent = await page.getTextContent();
                            const pageText = textContent.items.map(item => item.str).join(' ');
                            fullText += pageText + '\n';
                        }
                        resolve(fullText);
                    } catch (error) {
                        reject(error);
                    }
                };
                fileReader.readAsArrayBuffer(file);
            });

            // Get headers from apiHelper
            const headers = await apiHelper.getHeaders('daire');

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/daire/summariseContent`, {
                method: 'POST',
                headers: {
                    ...headers,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    text: pdfText,
                    title: title || formData.title,
                    type: "document"
                })
            });

            if (!response.ok) {
                throw new Error('Failed to generate summary');
            }

            const data = await response.json();
            return data.summary.choices[0].message.content;

        } catch (error) {
            console.error('Summary generation error:', error);
            throw error;
        } finally {
            setIsGeneratingSummary(false);
        }
    };

    // Update the AI summary effect - only for manual generation via Generate button
    useEffect(() => {
        // Clear summary when file changes
        if (formData.file) {
            setFormData(prev => ({
                ...prev,
                summary: '',
                useAiSummary: false
            }));
        }

        const generateSummary = async () => {
            if (formData.useAiSummary && formData.file) {
                try {
                    const summary = await generateSummaryFromPDF(formData.file, formData.title);
                    if (summary) {
                        setFormData(prev => ({
                            ...prev,
                            summary: summary,
                        }));
                    }
                } catch (error) {
                    toast.error('Failed to generate summary');
                    setFormData(prev => ({ ...prev, useAiSummary: false }));
                }
            }
        };

        generateSummary();
    }, [formData.useAiSummary, formData.file]);

    // Update useEffect to watch currentFile instead of formData.file
    useEffect(() => {
        if (currentFile) {
            setFormData(prev => ({
                ...prev,
                summary: '',
                useAiSummary: false
            }));
            setIsGeneratingSummary(false);
        }
    }, [currentFile]);

    // Static arrays for dropdowns
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 6 }, (_, i) => currentYear - 2 + i); // Current year ±2 years

    // Add this helper function at the top of the file
    const getQuarterFromMonth = (month) => {
        if (!month) return '';
        const monthIndex = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'].indexOf(month);
        if (monthIndex === -1) return '';
        return `Q${Math.floor(monthIndex / 3) + 1}`;
    };

    // Update handleInputChange to include auto-population of quarter
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name === 'month' && ['CBI_QTRLY', 'KEY_POLICY_MESSAGES'].includes(formData.mpKey)) {
            // Auto-populate quarter when month changes and MP Key is relevant
            setFormData(prev => ({
                ...prev,
                [name]: value,
                qtr: getQuarterFromMonth(value)
            }));
        } else if (name === 'mpKey') {
            // Reset quarter if MP Key changes to non-quarterly type
            setFormData(prev => ({
                ...prev,
                [name]: value,
                qtr: ['CBI_QTRLY', 'KEY_POLICY_MESSAGES'].includes(value) 
                    ? getQuarterFromMonth(prev.month) 
                    : ''
            }));
        } else if (name === 'status') {
            setFormData(prev => ({
                ...prev,
                status: value,
                publishDate: value === 'Published' ? prev.publishDate : null
            }));
        } else if (name === 'publish') {
            setFormData(prev => ({
                ...prev,
                publish: checked,
                status: checked ? 'Published' : 'Draft',
                publishDate: checked ? prev.publishDate : null
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.classList.remove('dragging');
        
        const file = e.dataTransfer.files[0];
        if (file) {
   
            
            const isPDF = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
            if (!isPDF) {
                toast.error('Only PDF files are allowed. Please drop a PDF document.', {
                    duration: 4000,
                    icon: '📄'
                });
                return;
            }

            // Set the new file
            setCurrentFile(file);

            // Reset form data
            const newFormData = {
                title: file.name.replace(/\.pdf$/i, '').replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                summary: '',
                type: '',
                workingGroup: '',
                file: file,
                fileName: file.name,
                month: '',
                year: new Date().getFullYear(),
                mpKey: '',
                qtr: '',
                status: 'Published',
                publish: true,
                publishDate: new Date().toISOString().split('T')[0],
                useAiSummary: false,
                doNotShowInSources: false
            };
            

            setFormData(newFormData);

            // Reset AI states
            setIsAiSummaryAvailable(true);
            setIsGeneratingSummary(false);
        }
    };

    const handleFileChange = (e) => {
        e.preventDefault();
        const file = e.target.files[0];
        if (file) handleNewFile(file);
    };

    const handleNewFile = (file) => {
        if (!file) return;
        
        const isPDF = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        if (!isPDF) {
            toast.error('Only PDF files are allowed');
            return;
        }

        setFormData(prev => ({
            ...prev,
            file: file,
            fileName: file.name,
            title: file.name.replace(/\.pdf$/i, '').replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            summary: '',
            useAiSummary: false
        }));
    };

    // Add drag and drop handlers
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.classList.add('dragging');
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.classList.remove('dragging');
    };

    const clearFile = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
        }
        
        setFormData(prev => ({
            ...prev,
            file: null,
            fileName: '',
            title: '',           // Clear the title
            summary: '',         // Clear the summary
            useAiSummary: false  // Reset AI summary
        }));
        setIsAiSummaryAvailable(false); // Disable AI summary option
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        setIsSubmitting(true);
        
        try {
            // Required fields validation
            if (!formData.file) {
                toast.error('Please select a file to upload');
                setIsSubmitting(false);
                return;
            }
            if (!formData.title) {
                toast.error('Please enter a title');
                setIsSubmitting(false);
                return;
            }
            if (!formData.type) {
                toast.error('Please select a document type');
                setIsSubmitting(false);
                return;
            }

            // Generate summary on upload if checkbox is checked
            let summary = formData.summary;
            if (generateSummaryOnUpload && formData.file) {
                try {
                    toast.info('Generating summary with Daire...');
                    summary = await generateSummaryFromPDF(formData.file, formData.title);
                    if (!summary) {
                        toast.warning('Summary generation failed, proceeding without summary');
                    }
                } catch (error) {
                    console.error('Summary generation error:', error);
                    toast.warning('Failed to generate summary, proceeding without summary');
                    // Continue with empty summary if generation fails
                }
            }
            
            // Create FormData with all metadata
            const uploadFormData = new FormData();
            
            // Add file and metadata
            uploadFormData.append('file', formData.file);
            uploadFormData.append('title', formData.title);
            uploadFormData.append('summary', summary || '');
            uploadFormData.append('type', formData.type);
            uploadFormData.append('workingGroup', formData.workingGroup);
            uploadFormData.append('year', formData.year.toString());
            uploadFormData.append('month', formData.month);
            uploadFormData.append('qtr', formData.qtr);
            uploadFormData.append('mpKey', formData.mpKey);
            uploadFormData.append('status', formData.status);
            uploadFormData.append('publish', formData.publish ? 'true' : 'false');
            uploadFormData.append('publishDate', formData.status === 'Published' ? (formData.publishDate || '') : '');

            // Call the upload handler
            const uploadedDocument = await onUpload(uploadFormData);

            // Upload completed successfully - indexing disabled
            
            // Close modal after successful upload
            onClose();
            
        } catch (error) {
            console.error('Upload failed:', error);
            toast.error(error.message || 'Failed to upload document');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRegenerateSummary = async () => {
        if (!formData.file) return;
        
        try {
            const summary = await generateSummaryFromPDF(formData.file, formData.title);
            if (summary) {
                setFormData(prev => ({
                    ...prev,
                    summary: summary,
                }));
            }
        } catch (error) {
            toast.error('Failed to regenerate summary');
        }
    };

    return (
        <div className="upload-modal-overlay" onClick={onClose}>
            <div className="upload-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2><FontAwesomeIcon icon={faUpload} /> Upload Document</h2>
                    <button className="close-button" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        <div className="form-group file-upload">
                            <label htmlFor="fileInput">Document File *</label>
                            
                          
                            <input
                                type="file"
                                id="fileInput"
                                name="fileInput"
                                onChange={handleFileChange}
                                accept=".pdf,application/pdf"
                                style={{ display: 'none' }}
                            />
                            
                            {/* Drag area */}
                            <div 
                                className="file-input-wrapper"
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={() => document.getElementById('fileInput').click()}
                            >
                                <div className="file-preview">
                                    {formData.fileName ? (
                                        <>
                                            <FontAwesomeIcon icon={faFile} />
                                            <span>{formData.fileName}</span>
                                            <button 
                                                type="button" 
                                                className="clear-file"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    clearFile(e);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        </>
                                    ) : (
                                        <span className="placeholder">Choose a file or drag it here</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Document Title *</label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                required
                                placeholder="Enter document title"
                            />
                        </div>

                        <div className="form-group">
                            <label>Summary</label>
                            <div className="summary-container">
                                {formData.file && !isGeneratingSummary && !formData.summary && (
                                    <button 
                                        type="button"
                                        className="generate-summary-btn"
                                        onClick={() => setFormData(prev => ({ ...prev, useAiSummary: true }))}
                                    >
                                        <FontAwesomeIcon icon={faRobot} /> Generate Summary with Daire
                                    </button>
                                )}
                                {isGeneratingSummary && (
                                    <div className="generating-summary">
                                        <FontAwesomeIcon icon={faSpinner} spin /> Daire is analyzing your document...
                                    </div>
                                )}
                                <textarea
                                    name="summary"
                                    value={formData.summary}
                                    onChange={handleInputChange}
                                    placeholder={isGeneratingSummary 
                                        ? "Daire is analyzing your document..." 
                                        : "Enter document summary or use Daire to generate one"}
                                    rows={3}
                                    disabled={isGeneratingSummary}
                                />
                                {formData.summary && !isGeneratingSummary && (
                                    <button 
                                        type="button"
                                        className="regenerate-btn"
                                        onClick={handleRegenerateSummary}
                                        title="Generate new summary"
                                    >
                                        <FontAwesomeIcon icon={faRotate} /> Regenerate Summary
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Document Type *</label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select Type</option>
                                    {documentTypes.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Working Group</label>
                                <select
                                    name="workingGroup"
                                    value={formData.workingGroup}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Select Working Group</option>
                                    {workingGroups.map(group => (
                                        <option key={group} value={group}>{group}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="doNotShowInSources"
                                        checked={formData.doNotShowInSources}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            doNotShowInSources: e.target.checked
                                        }))}
                                    />
                                    Do Not Show In Sources
                                </label>
                                <small className="field-description">
                                    When enabled, this document will not appear in source listings
                                </small>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Month</label>
                                <select
                                    name="month"
                                    value={formData.month}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Select Month</option>
                                    {months.map(month => (
                                        <option key={month} value={month}>{month}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Year *</label>
                                <select
                                    name="year"
                                    value={formData.year}
                                    onChange={handleInputChange}
                                    required
                                >
                                    {years.map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            
                            <div className="form-group">
                                <label>MP Key</label>
                                <select
                                    name="mpKey"
                                    value={formData.mpKey}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Select MP Key</option>
                                    {mpKeyOptions.map(key => (
                                        <option key={key} value={key}>{key}</option>
                                    ))}
                                </select>
                            </div>

                            {['CBI_QTRLY', 'KEY_POLICY_MESSAGES'].includes(formData.mpKey) && (
                                <div className="form-group">
                                    <label>Quarter *</label>
                                    <select
                                        name="qtr"
                                        value={formData.qtr}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select Quarter</option>
                                        {quarters.map(q => (
                                            <option key={q} value={q}>{q}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                        </div>

                        <div className="form-row publish-row">
                            <div className="form-group">
                                <label>Status</label>
                                <select
                                    name="publish"
                                    value={formData.publish ? 'Published' : 'Draft'}
                                    onChange={(e) => {
                                        const isPublished = e.target.value === 'Published';
                                        setFormData(prev => ({
                                            ...prev,
                                            publish: isPublished,
                                            status: isPublished ? 'Published' : 'Draft',
                                            publishDate: isPublished ? prev.publishDate : null
                                        }));
                                    }}
                                    className="status-select"
                                >
                                    <option value="Published">Published</option>
                                    <option value="Draft">Draft</option>
                                </select>
                            </div>

                            {formData.status === 'Published' && (
                                <div className="form-group publish-date">
                                    <label>Publish Date</label>
                                    <input
                                        type="date"
                                        name="publishDate"
                                        value={formData.publishDate || ''}
                                        onChange={handleInputChange}
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <div className="footer-checkbox">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={generateSummaryOnUpload}
                                    onChange={(e) => setGenerateSummaryOnUpload(e.target.checked)}
                                    disabled={!formData.file || isSubmitting}
                                />
                                <span>Generate summary with Daire on upload</span>
                            </label>
                        </div>
                        <div className="footer-buttons">
                            <button 
                                type="button" 
                                className="btn-secondary" 
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn-primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin /> Uploading...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faUpload} /> Upload Document
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UploadDocumentModal; 